import {memo, useCallback, useState} from "react";
import {NotificationChannel} from "../../proto/generated/common";
import {Box, Button, Dialog, DialogActions, DialogContent, Stack, Tab, Tabs, Typography} from "@mui/material";
import {selectMultiPaneFullScreen} from "../../selectors/common";
import {useDispatch, useSelector} from "react-redux";
import {MULTIPANE_FULLSCREEN_MODE_ID} from "../../assets/constants";
import AlertSettingsTab from "./alertSettingsTab/alertSettingsTab";
import {addUserAlert, removeAlertModalData, updateUserAlert} from "../../reducer/alerts.reducer";
import {selectAlertModalData} from "../../selectors/alerts";
import {createAlert} from "../../services/apis/alerts";
import {getCreateUserAlertRequestObject} from "../../util/alerts";
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import ClearIcon from "../../assets/icons/ClearIcon";
import {ExtraSmallButton} from "../custom";


const AlertModal: React.FC = () => {

    const dispatch = useDispatch();
    const container = useSelector(selectMultiPaneFullScreen)
    const {chartSnapshot, alert, edit} = useSelector(selectAlertModalData)
    const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([NotificationChannel.DESKTOP_POPUP])
    const [activeTab, setActiveTab] = useState('settings')
    const [error, setError] = useState(false)

    const handleTabChange = (_event: React.ChangeEvent<{}>, newTab: string) => {
        setActiveTab(newTab)
    }

    const closeModal = useCallback(() => {
        dispatch(removeAlertModalData())
    }, [])

    const createUserAlert = useCallback(async () => {
        if (!alert) return;
        const newAlert = await createAlert(getCreateUserAlertRequestObject(alert))
        if (edit) {
            dispatch(updateUserAlert(newAlert))
        } else {
            dispatch(addUserAlert(newAlert))
        }
        closeModal();
    }, [alert, edit])

    const onValidation = useCallback((error: boolean) => setError(error), [])

    return <Dialog
        open
        onClose={closeModal}
        fullWidth
        maxWidth={'xs'}
        container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
        aria-labelledby="alerts-dialog">
        <DraggableDialogTitle>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'h6'}>{`${edit ? "Edit" : "Create"} Alert`}</Typography>
                <ExtraSmallButton onClick={closeModal} aria-label="close" color={"inherit"}>
                    <ClearIcon/>
                </ExtraSmallButton>
            </Stack>
        </DraggableDialogTitle>
        <DialogContent>
            {notificationChannels.length > 1 ?
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="inherit"
                    >
                        <Tab label="Settings" value="settings"/>
                        <Tab label="Notifications" value="notifications"/>
                    </Tabs>
                </Box>
                :
                <></>
            }
            {
                activeTab === "settings" && <AlertSettingsTab onValidation={onValidation}/>
            }
        </DialogContent>
        <DialogActions>
            <Stack direction={'row'} alignItems={'right'} spacing={1}>
                <Button variant="outlined" onClick={closeModal}>
                    Cancel
                </Button>
                <Button autoFocus
                        disabled={error}
                        variant={'contained'}
                        data-event={`Alert ${edit ? 'Saved' : 'Created'}`}
                        data-event-alert-message={alert?.message}
                        data-event-alert-name={alert?.name}
                        onClick={createUserAlert}
                >
                    {edit ? 'Save' : 'Create'}
                </Button>
            </Stack>
        </DialogActions>
    </Dialog>
}
export default memo(AlertModal);